import { Modal, Text, Stack, Group, Divider } from "@mantine/core"
import { IconTrash } from "@tabler/icons-react"
import { PaginationState } from "@tanstack/react-table"
import { Button, EqualWidthGrid } from "@tfg/ui"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  findProductionSitesForSupplierQueryKey,
  FindProductionSitesForSupplierResponseDto,
  useDeleteProductionSite,
} from "~/gen/api"
import { formatAddress } from "~/utils/format-address"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface Props {
  isOpen: boolean
  onClose: () => void
  productionSite: FindProductionSitesForSupplierResponseDto
  pagination: PaginationState
}

export const DeleteProductionSiteModal = ({
  isOpen,
  onClose,
  productionSite,
  pagination,
}: Props) => {
  const { t } = useTranslation("supplier")

  const { mutate: deleteProductionSite, isPending: isLoading } =
    useDeleteProductionSite({
      mutation: {
        onSuccess: () => {
          onClose()
          showSuccessNotification({
            message: t("productionSites.delete.success.message"),
            title: t("productionSites.delete.success.title"),
          })
          queryClient.invalidateQueries({
            queryKey: findProductionSitesForSupplierQueryKey({
              pagination: {
                take: pagination.pageSize,
                skip: pagination.pageIndex,
              },
            }),
          })
        },
        onError: (error: AxiosError) => {
          showAxiosError({
            notificationTitle: t("productionSites.delete.error"),
            notificationMessage: error.message,
            showNotification: true,
            error,
          })
        },
      },
    })

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Stack>
          <Text fw={700} size="lg">
            {t("productionSiteModal.delete.title")}
          </Text>
        </Stack>
      }
    >
      <Stack>
        <Divider w="100%" />
        <Stack>
          <Text>{t("productionSiteModal.delete.message")}</Text>
          <Group gap="var(--mantine-spacing-xxs)">
            <Text fw={700}>{productionSite.name}</Text>
            <Text size="xs">{formatAddress(productionSite)}</Text>
          </Group>
        </Stack>
        <EqualWidthGrid>
          <Button
            variant="secondary-critical"
            onClick={() => onClose()}
            fullWidth={true}
          >
            {t("productionSiteModal.delete.cancel")}
          </Button>
          <Button
            onClick={() => {
              deleteProductionSite({ productionSiteId: productionSite.id })
              onClose()
            }}
            loading={isLoading}
            fullWidth={true}
            color="red"
            icon={<IconTrash />}
          >
            {t("productionSiteModal.delete.confirm")}
          </Button>
        </EqualWidthGrid>
      </Stack>
    </Modal>
  )
}
